import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  deleteDoc, 
  getDoc,
  query, 
  orderBy,
  where 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION_NAME = 'discount_codes';

export const firebaseDiscountService = {
  // Obtener todos los códigos de descuento
  async getAll() {
    try {
      const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const discounts = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        discounts.push({
          id: doc.id,
          ...data,
          // Convertir timestamps de Firebase a Date
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          expiresAt: data.expiresAt?.toDate?.() || data.expiresAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt
        });
      });

      console.log('🎫 Códigos de descuento obtenidos:', discounts.length);
      return discounts;
    } catch (error) {
      console.error('Error al obtener códigos de descuento:', error);
      throw error;
    }
  },

  // Crear o actualizar código de descuento
  async createOrUpdate(discountData) {
    try {
      const { id, ...data } = discountData;
      const docId = id || data.code.toUpperCase();
      
      const discountToSave = {
        ...data,
        code: data.code.toUpperCase(),
        updatedAt: new Date(),
        createdAt: data.createdAt || new Date(),
        usedCount: data.usedCount || 0
      };

      await setDoc(doc(db, COLLECTION_NAME, docId), discountToSave);
      
      console.log('✅ Código de descuento guardado:', docId);
      return { id: docId, ...discountToSave };
    } catch (error) {
      console.error('Error al guardar código de descuento:', error);
      throw error;
    }
  },

  // Eliminar código de descuento
  async delete(id) {
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
      console.log('🗑️ Código de descuento eliminado:', id);
      return true;
    } catch (error) {
      console.error('Error al eliminar código de descuento:', error);
      throw error;
    }
  },

  // Validar y aplicar código de descuento
  async validateAndApply(code, orderValue) {
    try {
      const docRef = doc(db, COLLECTION_NAME, code.toUpperCase());
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return { 
          valid: false, 
          error: 'Código de descuento no encontrado',
          discount: 0 
        };
      }

      const discount = docSnap.data();
      const now = new Date();

      // Verificar si está activo
      if (!discount.active) {
        return { 
          valid: false, 
          error: 'Código de descuento no activo',
          discount: 0 
        };
      }

      // Verificar fecha de expiración
      const expiresAt = discount.expiresAt?.toDate?.() || discount.expiresAt;
      if (expiresAt && now > expiresAt) {
        return { 
          valid: false, 
          error: 'Código de descuento expirado',
          discount: 0 
        };
      }

      // Verificar límite de usos
      if (discount.maxUses && discount.usedCount >= discount.maxUses) {
        return { 
          valid: false, 
          error: 'Código de descuento agotado',
          discount: 0 
        };
      }

      // Verificar monto mínimo
      if (discount.minOrderValue && orderValue < discount.minOrderValue) {
        return { 
          valid: false, 
          error: `Pedido mínimo de €${discount.minOrderValue} requerido`,
          discount: 0 
        };
      }

      // Calcular descuento
      let discountAmount = 0;
      if (discount.type === 'percentage') {
        discountAmount = (orderValue * discount.value) / 100;
        if (discount.maxDiscount) {
          discountAmount = Math.min(discountAmount, discount.maxDiscount);
        }
      } else if (discount.type === 'fixed') {
        discountAmount = discount.value;
      }

      // No puede ser mayor que el valor del pedido
      discountAmount = Math.min(discountAmount, orderValue);

      return {
        valid: true,
        discount: discountAmount,
        code: discount.code,
        description: discount.description,
        type: discount.type,
        value: discount.value
      };

    } catch (error) {
      console.error('Error al validar código de descuento:', error);
      return { 
        valid: false, 
        error: 'Error al procesar el código',
        discount: 0 
      };
    }
  },

  // Marcar código como usado (incrementar contador)
  async markAsUsed(code) {
    try {
      const docRef = doc(db, COLLECTION_NAME, code.toUpperCase());
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        await setDoc(docRef, {
          ...currentData,
          usedCount: (currentData.usedCount || 0) + 1,
          lastUsedAt: new Date()
        });
        
        console.log('📊 Código marcado como usado:', code);
      }
    } catch (error) {
      console.error('Error al marcar código como usado:', error);
    }
  },

  // Obtener códigos activos
  async getActiveCodes() {
    try {
      const q = query(
        collection(db, COLLECTION_NAME), 
        where('active', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const activeCodes = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const expiresAt = data.expiresAt?.toDate?.() || data.expiresAt;
        const now = new Date();
        
        // Solo incluir códigos no expirados
        if (!expiresAt || now <= expiresAt) {
          activeCodes.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || data.createdAt,
            expiresAt: expiresAt,
            updatedAt: data.updatedAt?.toDate?.() || data.updatedAt
          });
        }
      });

      return activeCodes;
    } catch (error) {
      console.error('Error al obtener códigos activos:', error);
      throw error;
    }
  }
};

// Datos iniciales para códigos de descuento
export const defaultDiscountCodes = [
  {
    id: 'BIENVENIDO10',
    code: 'BIENVENIDO10',
    description: 'Descuento de bienvenida del 10%',
    type: 'percentage',
    value: 10,
    minOrderValue: 30,
    maxDiscount: 15,
    maxUses: 100,
    active: true,
    expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) // 1 año
  },
  {
    id: 'ENVIOGRATIS',
    code: 'ENVIOGRATIS',
    description: 'Envío gratuito',
    type: 'fixed',
    value: 4.95,
    minOrderValue: 25,
    maxUses: 50,
    active: true,
    expiresAt: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000) // 6 meses
  }
];

// Función para inicializar códigos por defecto
export const initializeDiscountCodes = async () => {
  try {
    const existingCodes = await firebaseDiscountService.getAll();
    
    if (existingCodes.length === 0) {
      console.log('🎫 Inicializando códigos de descuento por defecto...');
      
      for (const code of defaultDiscountCodes) {
        await firebaseDiscountService.createOrUpdate(code);
      }
      
      console.log('✅ Códigos de descuento inicializados');
    }
  } catch (error) {
    console.error('Error al inicializar códigos de descuento:', error);
  }
}; 